import { useEffect, useState } from 'react'
import { firestore } from '../../firebase'
import { SearchOutlined } from '@ant-design/icons'
import { Table, Input, Space, Button, Statistic, Row, Col, Card, Divider, Typography, Skeleton, Tooltip } from 'antd'
import { CSVLink } from 'react-csv'
import Highlighter from 'react-highlight-words'
import moment from 'moment'
import DateRangeSelector from '../dateRangeSelector'
import { sourceNameLookup } from '../../helpers/sources'

const { Title } = Typography

const DEDUPE_FIELDS = [
  'email_id',
  'message_type',
  'message_id',
  'subscriber_id',
  'subscriber_offer',
  'subscriber_source',
  'subscriber_email',
  'responder_id',
  'responder_variation',
  'publication_id'
]

const getSourceDisplay = affId => {
  const sourceName = sourceNameLookup({ affId })
  const hasKnownSource = sourceName && sourceName !== `n/a (${affId})`

  return {
    source_name: hasKnownSource ? sourceName : `n/a (affiliate ${affId})`,
    source_title: hasKnownSource ? `${sourceName} (affiliate ${affId})` : `(affiliate ${affId})`
  }
}

let searchInput = null

const OptiFeebackBounces = () => {

  const [isFetching, setIsFetching] = useState(false)
  const [groupedPartners, setGroupedPartners] = useState([])
  const [bounces, setBounces] = useState([])
  const [searchText, setSearchText] = useState(null)
  const [searchedColumn, setSearchedColumn] = useState(null)
  const [fetchStartDate, setFetchStartDate] = useState(null)
  const [fetchEndDate, setFetchEndDate] = useState(null)

  useEffect(() => {

    if (fetchStartDate && fetchEndDate) fetchHistory()

  }, [fetchStartDate, fetchEndDate])

  const fetchHistory = async () => {

    setIsFetching(true)

    try {

      // list of conversions
      const res = await firestore.collection('feedback')
        .where('created_at', '>=', new Date(fetchStartDate.startOf('d').valueOf()))
        .where('created_at', '<=', new Date(fetchEndDate.endOf('d').valueOf()))
        .where('type', '==', 'hard bounce')
        .orderBy('created_at', 'desc')
        .get()

      if (res.empty) return

      // --- ALL ----
      const tableData = res.docs.map((x, i) => {
        const sourceDisplay = getSourceDisplay(x.data()?.subscriber_source)

        return {
          ...x.data(),
          date: moment(x.data().created_at.toDate()).format('MM/DD/YY h:mm A'),
          ...sourceDisplay,
          mail_type: x.data()?.message_type || x.data()?.message_id > 0 ? 'Message' : 'AR',
          mail_id: x.data()?.message_id || x.data()?.responder_variation,
          key: i
        }
      })

      const dedupedTableData = Array.from(
        tableData.reduce((acc, bounce) => {
          const dedupeKey = DEDUPE_FIELDS
            .map((field) => JSON.stringify(bounce[field]))
            .join('|')

          if (!acc.has(dedupeKey)) acc.set(dedupeKey, bounce)

          return acc
        }, new Map()).values()
      ).map((bounce, index) => ({
        ...bounce,
        key: index
      }))

      
      // --- GROUPED ---
      const grouped = dedupedTableData
        .reduce((acc, obj) => {

          const category = obj.subscriber_source
          const existingArray = acc.find((arr) => arr[0]?.subscriber_source === category)
        
          if (existingArray) {
            existingArray.push(obj)
          } else {
            acc.push([obj])
          }
        
          return acc
        }, [])

      setGroupedPartners(grouped)
      setBounces(dedupedTableData)

    } catch (error) {

      console.error({error})

    } finally {

      // remove loading
      setIsFetching(false)
    }
  }

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={node => { searchInput = node }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size='small' style={{ width: 90 }}>
            Reset
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText(selectedKeys[0])
              setSearchedColumn(dataIndex)
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    render: text =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = clearFilters => {
    clearFilters()
    setSearchText('')
  }

  const renderSourceName = record => {
    const sourceName = searchedColumn === 'source_name' ? (
      <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={record.source_name ? record.source_name.toString() : ''}
      />
    ) : (
      record.source_name
    )

    return (
      <Tooltip title={record.source_title}>
        {sourceName}
      </Tooltip>
    )
  }

  const columnsBounces = [
    {
      width: 150,
      key: 'date',
      title: 'Date',
      dataIndex: 'date',
      defaultSortOrder: 'descend',
      sorter: (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()
    }, {
      width: 200,
      key: 'subscriber_source',
      title: 'Source',
      dataIndex: 'source_name',
      sorter: (a, b) => a.source_name.localeCompare(b.source_name),
      ...getColumnSearchProps('source_name'),
      render: (_, record) => renderSourceName(record)
    }, {
      width: 250,
      key: 'subscriber_email',
      title: 'Email',
      dataIndex: 'subscriber_email'
    }, {
      width: 250,
      key: 'mail_type',
      title: 'Type',
      dataIndex: 'mail_type'
    }, {
      width: 250,
      key: 'mail_id',
      title: 'Message / Variation ID',
      dataIndex: 'mail_id'
    }
  ]

  return (
    <>
      <Title level={2}>OptiPub Bounces</Title>

      <Row gutter={[16, 16]} justify='end' align='center'>
        <Col sm={8} md={4} >
          <Button
            type='primary'
            loading={isFetching}
            onClick={fetchHistory}
          >
            Refresh Data
          </Button>
        </Col>
      </Row>

      {(isFetching) &&
        <Skeleton active={true} paragraph={{ rows: 1 }} style={{ marginTop: '-1.25rem' }} />
      }

      {!(isFetching) &&
        <Row gutter={[16, 16]} justify='space-between' align='middle'>
          <Col xm={24} sm={16} style={{ display: 'flex', flexDirection: 'column' }}>
            <DateRangeSelector fetchStartDate={fetchStartDate} fetchEndDate={fetchEndDate} onChange={({ startDate, endDate }) => {
              setBounces([])
              setGroupedPartners([])
              setFetchStartDate(startDate)
              setFetchEndDate(endDate)
            }} />
          </Col>
        </Row>
      }

      {bounces.length > 0 &&
        <Col>
          <Card variant='borderless'>
            <Statistic title='Total' value={bounces.length} />
          </Card>
        </Col>
      }

      {groupedPartners.length > 0 &&
        <>
          <Divider orientation="left">Sources</Divider>
          <Row gutter={[16, 16]}>
            {groupedPartners.sort((a,b) => b.length - a.length).map((x, i) =>
              <Col key={i} span={4}>
                <Card
                  size='small'
                  title={<Tooltip title={x[0].source_title}>{x[0].source_name}</Tooltip>}
                >
                  <Statistic value={x.length} />
                </Card>
              </Col>
            )}
          </Row>
        </>
      }

      <Row justify='space-between' align='middle'>
        <Table
          style={{ paddingTop: 20 }}
          onChange={(pagination, filters, sorter, extra) => setBounces(extra.currentDataSource)}
          loading={isFetching}
          columns={columnsBounces}
          dataSource={bounces}
          scroll={{ x: 1000, y: 500 }}
          size='small'
          title={() => (
            <CSVLink
              filename={"OptiPub_Bounces.csv"}
              data={bounces}
              className="btn btn-primary"
            >
              Export to CSV
            </CSVLink>
          )}
        />
      </Row>
    </>
  )
}

export default OptiFeebackBounces
